/** @jsx jsx */
import { useEffect, FC, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
} from 'react-router-dom';
import { useCookies } from 'react-cookie';
import { Helmet } from 'react-helmet';
import ReactGA from 'react-ga4';
import { Global, jsx } from '@emotion/core';

import useAuthToken, { AUTH_COOKIE_KEY } from 'utils/useAuthToken';
import { globalStyles } from 'global-styles';
import {
  Auth,
  Content,
  ContentCategory,
  NotFound,
  ChallengeDeclaration,
  HistoryOfDeclaredChallenges,
  DetailsPageDataLoader,
} from 'pages';
import Notifier from 'components/layouts/Notifier';
import Footer from 'components/layouts/Footer';
import Header from 'components/layouts/Header';
import Loader from 'components/layouts/Loader';

import {
  appOptionsSelector,
  appReducerSelector,
  tokenSelector,
  authLoadingSelector,
  loginLoadingSelector,
} from 'reducers/app.reducer';
import {
  appConfig,
  appOptions,
  checkAuth,
  productCurrency,
} from 'actions/app.actions';
import { setAuthToken } from 'services/api';
import { AppAPI } from 'services/app.api';
import { ICustomTheme, StoreOptionsType } from 'interfaces';
import { ROUTES } from '../constants';
import { CookiesInfos } from 'components/CookiesInfos';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { StyledContainer } from './styles';

// Set to '_cle' to be in sync with the v1 cookie management key
// This way both v1 and v2 use the same cookie to know if the cookie banner has been considered
export const ESSENTIAL_COOKIES = '_cle';

type Props = {
  component: FC;
  [key: string]: unknown;
};

export const PrivateRoute = ({
  component: Component,
  ...rest
}: Props): JSX.Element => {
  const token: string = useSelector(tokenSelector);
  const isAuthenticated = !!token?.length;

  const location = rest.location as { pathname: string };

  if (!isAuthenticated) {
    if (location.pathname === ROUTES.HOME)
      return <Redirect to={ROUTES.LOGIN} />;

    const encodedReturnPath = encodeURIComponent(
      window.location.href.slice(window.location.origin.length),
    );

    return <Redirect to={`${ROUTES.LOGIN}?returnPath=${encodedReturnPath}`} />;
  }

  return <Route {...rest} component={Component} />;
};

const URL_AUTH_TOKEN = 'authToken';
function useTokenHandler() {
  const dispatch = useDispatch();
  const { cookie, setCookie } = useAuthToken();

  const urlParams = new URLSearchParams(window.location.search);
  const urlToken = urlParams.get(URL_AUTH_TOKEN);

  // handle URL token
  useEffect(() => {
    if (urlToken) {
      setCookie(urlToken);

      // delete the token from the URL
      urlParams.delete(URL_AUTH_TOKEN);
      window.history.pushState(
        null,
        '',
        `${window.location.pathname}${urlParams.toString()}`,
      );
    }
  }, [urlToken, setCookie, urlParams]);

  const token = urlToken ?? cookie[AUTH_COOKIE_KEY];
  useEffect(() => {
    if (token) {
      setAuthToken(token);
      dispatch(checkAuth.request(token));
    }
  }, [token, dispatch]);
}

export const App = (): JSX.Element => {
  useTokenHandler();

  const dispatch = useDispatch();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { loading: appLoading } = useSelector(appReducerSelector);
  const authLoading = useSelector(authLoadingSelector);
  const loginLoading = useSelector(loginLoadingSelector);
  const options: StoreOptionsType = useSelector(appOptionsSelector);
  const title = options.list?.find(
    (option) => option.name === 'UI_META_TITLE',
  )?.value;
  const [cookie, setCookie] = useCookies([ESSENTIAL_COOKIES]);
  const [oneTrustKey, setOneTrustKey] = useState<null | string>(null);
  const isEssentialCookieConsidered = !!cookie[ESSENTIAL_COOKIES];
  const isLoginPage = window.location.pathname.includes(ROUTES.LOGIN);

  const theme: ICustomTheme = useTheme();
  const isDesktop = !useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    dispatch(appConfig.request());
    dispatch(appOptions.request());
    dispatch(productCurrency.request());
    AppAPI.getOneTrustKey().then((resp) => {
      const key = resp.data.keyOneTrust;
      setOneTrustKey(
        process.env.NODE_ENV === 'production' ? key : `${key}-test`,
      );
    });

    AppAPI.getGAConfig().then((response) => {
      const measurementId = response.data.ua;
      if (measurementId) {
        ReactGA.initialize(measurementId);
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (appLoading || authLoading || options.isLoading || loginLoading) {
    return <Loader label={t('auth.login.logging_in')} />;
  }

  const cookiesBanner = oneTrustKey ? (
    <Helmet>
      <script
        src={`https://cdn.cookielaw.org/consent/${oneTrustKey}/OtAutoBlock.js`}
        type="text/javascript"
      ></script>
      <script
        src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
        type="text/javascript"
        charSet="UTF-8"
        data-domain-script={oneTrustKey}
      ></script>
      <script type="text/javascript">function OptanonWrapper() {}</script>
    </Helmet>
  ) : (
    <CookiesInfos
      isEssentialCookieConsidered={isEssentialCookieConsidered}
      setCookie={setCookie}
    />
  );

  return (
    <StyledContainer isDesktop={isDesktop} isLoginPage={isLoginPage}>
      <Router basename={ROUTES.BASENAME}>
        <Helmet>
          <html lang={language || 'fr'} />
          {title && <title>{title}</title>}
        </Helmet>
        <Global styles={globalStyles} />
        <Notifier />
        <Header />
        <Switch>
          <Route path={ROUTES.LOGIN} component={Auth} />
          <PrivateRoute path={ROUTES.HOME} exact component={Content} />
          <PrivateRoute path={`${ROUTES.CONTENT}/:id`} component={Content} />
          <PrivateRoute
            path={`${ROUTES.CATEGORY_CONTENTS}/:id`}
            component={ContentCategory}
          />
          <PrivateRoute
            path={`${ROUTES.CHALLENGE}/:id${ROUTES.DECLARE}`}
            component={ChallengeDeclaration}
          />
          <PrivateRoute
            path={`${ROUTES.CHALLENGE}${ROUTES.HISTORY}/:id?`}
            component={HistoryOfDeclaredChallenges}
          />
          <PrivateRoute
            path={`${ROUTES.CHALLENGE}/:id`}
            component={DetailsPageDataLoader}
          />
          <PrivateRoute component={NotFound} />
        </Switch>
        {cookiesBanner}
        <Footer />
      </Router>
    </StyledContainer>
  );
};

export default App;
